import {useMemo} from 'react'
import {useCustomerType, useProduct, useProducts} from '@salesforce/commerce-sdk-react'
import {
    getMasterProductId,
    getPreferedVariant,
    getProductsToAddToCart,
    isCustomizableProductSet,
    isProductMaster,
    isProductSet,
    isProductVariant
} from '../utils/product-utils'
import {useLocation} from 'react-router-dom'
import {unifyProduct} from './use-unified-product'
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useProductSetFullPrices} from './useScapiCustomApi'
import useMultiSite from './use-multi-site'
import {MAX_IDS_PER_GET_PRODUCTS_REQUEST} from '../constants'
import {isHydrated, isServer} from '../utils/utils'

// default parameters to get product prices using the currently logged in customer
export const DEFAULT_GET_PRODUCT_PRICE_PARAMETERS = {
    // minimize response to only promotions
    allImages: false,
    expand: ['prices', 'promotions'],
    select: '(c_taxRate,price,productPromotions.(**))'
}

// default parameters to get product details - used for master/variant/product set
const DEFAULT_GET_PRODUCT_PARAMETERS = {
    allImages: true,
    expand: [
        'availability',
        'promotions',
        'images',
        'prices',
        'recommendations',
        'variations',
        'set_products'
    ]
}

// default parameters to get products details - used for set products
const DEFAULT_GET_PRODUCTS_PARAMETERS = {
    ...DEFAULT_GET_PRODUCT_PARAMETERS,
    expand: DEFAULT_GET_PRODUCT_PARAMETERS.expand.filter((e) => e !== 'set_products')
}

const useProductWithCustomerPrice = (productQueryResult, queryOptions) => {
    const {isRegistered} = useCustomerType()
    const product = productQueryResult.data
    // only if the product query is executed on ssr and we have a registered user
    // then we need to refetch (which only happens on client)
    const needsFetchingCustomerPrice =
        !!product?.id && isRegistered && !productQueryResult.isFetchedAfterMount

    const productCustomerPriceQueryResult = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PRICE_PARAMETERS,
                id: product?.id
            }
        },
        {
            keepPreviousData: false,
            ...queryOptions,
            enabled: queryOptions?.enabled !== false && needsFetchingCustomerPrice
        }
    )
    const productCustomerPrice = productCustomerPriceQueryResult.data

    return {
        ...(needsFetchingCustomerPrice ? productCustomerPriceQueryResult : productQueryResult),
        data: useMemo(
            () =>
                productCustomerPrice
                    ? {
                          ...product,
                          ...productCustomerPrice
                      }
                    : product,
            [product, productCustomerPrice]
        )
    }
}

const useProductDetailFromVariant = (variantQueryResult, queryOptions) => {
    const masterProductId = isProductVariant(variantQueryResult.data)
        ? getMasterProductId(variantQueryResult.data)
        : null
    const {data: master, isLoading: isMasterLoading} = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PARAMETERS,
                id: masterProductId
            }
        },
        {
            keepPreviousData: false,
            ...queryOptions,
            enabled: queryOptions?.enabled !== false && !!masterProductId
        }
    )

    // we know that prices come from the variant, so no need to refetch the master
    const {data: variant, isLoading: isPriceLoading} = useProductWithCustomerPrice(
        variantQueryResult,
        queryOptions
    )

    return {
        master,
        variant,
        isMasterLoading,
        isVariantLoading: variantQueryResult.isLoading,
        isPriceLoading
    }
}

const useProductDetailFromMaster = ({data: master, isLoading: isMasterLoading}, queryOptions) => {
    const pickedVariant = useMemo(
        () => (isProductMaster(master) ? getPreferedVariant(master) : null),
        [master]
    )
    const variantProductQueryResult = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PARAMETERS,
                id: pickedVariant?.productId
            }
        },
        {
            keepPreviousData: false,
            ...queryOptions,
            enabled: queryOptions?.enabled !== false && !!pickedVariant?.productId
        }
    )

    // we know that prices come from the variant, so no need to refetch the master
    const {data: variant, isLoading: isPriceLoading} = useProductWithCustomerPrice(
        variantProductQueryResult,
        queryOptions
    )

    return {
        master,
        variant,
        isMasterLoading,
        // without picked variant - variant and price loading is not needed
        isVariantLoading: pickedVariant ? variantProductQueryResult.isLoading : false,
        isPriceLoading: pickedVariant ? isPriceLoading : false
    }
}

const useProductDetailFromProductSet = (
    {data: productSet, isLoading: isProductSetLoading},
    queryOptions
) => {
    const {
        locale: {id: localeId}
    } = useMultiSite()
    const location = useLocation()
    const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search])

    // add the setProductId and isSetProductSelected to each set product and convert customizable set products to a set product like structure
    // {id, setProduct, isSetProductSelected}
    const productSetWithSystemSetProductProps = useMemo(() => {
        if (!isProductSet(productSet)) {
            return
        }

        const convert = (setProduct, setProductId, isDefaultSelected) => {
            const isSetProductSelected =
                isDefaultSelected ||
                new URLSearchParams(urlParams.get(setProductId) || '').get('selected') === '1'
            return {
                ...setProduct,
                setProductId,
                isSetProductSelected
            }
        }

        return {
            ...productSet,
            setProducts: productSet.setProducts?.map((sp, index) =>
                convert(sp, `${sp.id}_${index}`, true)
            ),
            ...(isCustomizableProductSet(productSet)
                ? {
                      setExtensions: productSet.c_extensionProductIds?.map((id, index) =>
                          convert({id}, `${id}_e${index}`)
                      ),
                      setAccessories: productSet.c_accessoriesProductIds?.map((id, index) =>
                          convert({id}, `${id}_a${index}`)
                      )
                  }
                : {})
        }
    }, [productSet, urlParams])

    // get the ids of the referenced set products
    const referencedSetProducts = useMemo(() => {
        const referencedSetProducts = [
            ...(productSetWithSystemSetProductProps?.setProducts || []),
            ...(productSetWithSystemSetProductProps?.setExtensions || []),
            ...(productSetWithSystemSetProductProps?.setAccessories || [])
        ]

        return Object.fromEntries(
            referencedSetProducts
                .map((sp) => {
                    if (!sp?.setProductId) {
                        return
                    }

                    const productParams = new URLSearchParams(urlParams.get(sp.setProductId) || '')

                    return [sp.setProductId, productParams.get('pid') || sp.id]
                })
                .filter(Boolean)
        )
    }, [productSetWithSystemSetProductProps, urlParams])

    const referencedSetProductIds = useMemo(
        () => [...new Set(Object.values(referencedSetProducts))],
        [referencedSetProducts]
    )

    // fetch the referenced set products
    const referencedSetProductsQueryResult1 = useProducts(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCTS_PARAMETERS,
                ids: referencedSetProductIds?.slice(0, MAX_IDS_PER_GET_PRODUCTS_REQUEST).join(',')
            }
        },
        {
            keepPreviousData: true,
            ...queryOptions,
            enabled: queryOptions?.enabled !== false && referencedSetProductIds?.length > 0
        }
    )
    const referencedSetProductsQueryResult2 = useProducts(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCTS_PARAMETERS,
                ids: referencedSetProductIds
                    ?.slice(MAX_IDS_PER_GET_PRODUCTS_REQUEST, 2 * MAX_IDS_PER_GET_PRODUCTS_REQUEST)
                    .join(',')
            }
        },
        {
            keepPreviousData: true,
            ...queryOptions,
            enabled:
                queryOptions?.enabled !== false &&
                referencedSetProductIds?.length > MAX_IDS_PER_GET_PRODUCTS_REQUEST
        }
    )
    const referencedSetProductsQueryResult = useMemo(
        () => ({
            data: {
                data: [
                    ...(referencedSetProductsQueryResult1.data?.data || []),
                    ...(referencedSetProductsQueryResult2.data?.data || [])
                ]
            },
            isLoading:
                referencedSetProductsQueryResult1.isLoading &&
                referencedSetProductsQueryResult2.isLoading,
            isFetching:
                referencedSetProductsQueryResult1.isFetching ||
                referencedSetProductsQueryResult2.isFetching,
            isInitialLoading:
                referencedSetProductsQueryResult1.isInitialLoading ||
                referencedSetProductsQueryResult2.isInitialLoading,
            isError:
                referencedSetProductsQueryResult1.isError ||
                referencedSetProductsQueryResult2.isError,
            error:
                referencedSetProductsQueryResult1.error || referencedSetProductsQueryResult2.error
        }),
        [
            referencedSetProductsQueryResult1.data,
            referencedSetProductsQueryResult2.data,
            referencedSetProductsQueryResult1.isLoading,
            referencedSetProductsQueryResult2.isLoading,
            referencedSetProductsQueryResult1.isFetching,
            referencedSetProductsQueryResult2.isFetching,
            referencedSetProductsQueryResult1.isInitialLoading,
            referencedSetProductsQueryResult2.isInitialLoading,
            referencedSetProductsQueryResult1.isError,
            referencedSetProductsQueryResult2.isError,
            referencedSetProductsQueryResult1.error,
            referencedSetProductsQueryResult2.error
        ]
    )

    // create a map of the referenced set products by their id
    const referencedSetProductsById = useMemo(
        () =>
            referencedSetProductsQueryResult.data?.data?.length > 0
                ? Object.fromEntries(
                      referencedSetProductsQueryResult.data.data.map((sp) => [sp.id, sp])
                  )
                : null,
        [referencedSetProductsQueryResult.data]
    )

    // add the referenced set products to the set products
    const productSetWithLoadedReferencedSetProducts = useMemo(() => {
        if (!isProductSet(productSetWithSystemSetProductProps) || !referencedSetProductsById) {
            return productSetWithSystemSetProductProps
        }

        const merge = (sp) => ({
            ...sp,
            ...referencedSetProductsById[referencedSetProducts[sp.setProductId]]
        })

        return {
            ...productSetWithSystemSetProductProps,
            setProducts: productSetWithSystemSetProductProps.setProducts?.map(merge),
            ...(isCustomizableProductSet(productSetWithSystemSetProductProps)
                ? {
                      setExtensions: productSetWithSystemSetProductProps.setExtensions?.map(merge),
                      setAccessories: productSetWithSystemSetProductProps.setAccessories?.map(merge)
                  }
                : {})
        }
    }, [productSetWithSystemSetProductProps, referencedSetProductsById, referencedSetProducts])

    // map from set product id to master or default variant id
    const masterOrVariantOfReferencedSetProducts = useMemo(
        () =>
            referencedSetProductsQueryResult.data?.data?.length > 0
                ? Object.fromEntries(
                      referencedSetProductsQueryResult.data.data
                          .map((sp) => {
                              if (isProductVariant(sp)) {
                                  return [sp.id, getMasterProductId(sp)]
                              }
                              if (isProductMaster(sp)) {
                                  const pickedVariant = getPreferedVariant(sp)
                                  return [sp.id, pickedVariant?.productId]
                              }
                              return null
                          })
                          .filter(Boolean)
                  )
                : null,
        [referencedSetProductsQueryResult.data]
    )

    // list of master or variant ids of the referenced set products
    const masterOrVariantOfReferencedSetProductIds = useMemo(
        () =>
            masterOrVariantOfReferencedSetProducts
                ? [...new Set(Object.values(masterOrVariantOfReferencedSetProducts))]
                : null,
        [masterOrVariantOfReferencedSetProducts]
    )

    // fetch the master or variant of the referenced set products
    const masterOrVariantOfReferencedSetProductsQueryResult1 = useProducts(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCTS_PARAMETERS,
                ids: masterOrVariantOfReferencedSetProductIds
                    ?.slice(0, MAX_IDS_PER_GET_PRODUCTS_REQUEST)
                    .join(',')
            }
        },
        {
            keepPreviousData: true,
            ...queryOptions,
            enabled:
                queryOptions?.enabled !== false &&
                masterOrVariantOfReferencedSetProductIds?.length > 0
        }
    )
    const masterOrVariantOfReferencedSetProductsQueryResult2 = useProducts(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCTS_PARAMETERS,
                ids: masterOrVariantOfReferencedSetProductIds
                    ?.slice(MAX_IDS_PER_GET_PRODUCTS_REQUEST, 2 * MAX_IDS_PER_GET_PRODUCTS_REQUEST)
                    .join(',')
            }
        },
        {
            keepPreviousData: true,
            ...queryOptions,
            enabled:
                queryOptions?.enabled !== false &&
                masterOrVariantOfReferencedSetProductIds?.length > MAX_IDS_PER_GET_PRODUCTS_REQUEST
        }
    )
    const masterOrVariantOfReferencedSetProductsQueryResult = useMemo(
        () => ({
            data: {
                data: [
                    ...(masterOrVariantOfReferencedSetProductsQueryResult1.data?.data || []),
                    ...(masterOrVariantOfReferencedSetProductsQueryResult2.data?.data || [])
                ]
            },
            isLoading:
                masterOrVariantOfReferencedSetProductsQueryResult1.isLoading ||
                masterOrVariantOfReferencedSetProductsQueryResult2.isLoading,
            isFetching:
                masterOrVariantOfReferencedSetProductsQueryResult1.isFetching ||
                masterOrVariantOfReferencedSetProductsQueryResult2.isFetching,
            isInitialLoading:
                masterOrVariantOfReferencedSetProductsQueryResult1.isInitialLoading ||
                masterOrVariantOfReferencedSetProductsQueryResult2.isInitialLoading,
            isError:
                masterOrVariantOfReferencedSetProductsQueryResult1.isError ||
                masterOrVariantOfReferencedSetProductsQueryResult2.isError,
            error:
                masterOrVariantOfReferencedSetProductsQueryResult1.error ||
                masterOrVariantOfReferencedSetProductsQueryResult2.error
        }),
        [
            masterOrVariantOfReferencedSetProductsQueryResult1.data,
            masterOrVariantOfReferencedSetProductsQueryResult2.data,
            masterOrVariantOfReferencedSetProductsQueryResult1.isLoading,
            masterOrVariantOfReferencedSetProductsQueryResult2.isLoading,
            masterOrVariantOfReferencedSetProductsQueryResult1.isFetching,
            masterOrVariantOfReferencedSetProductsQueryResult2.isFetching,
            masterOrVariantOfReferencedSetProductsQueryResult1.isInitialLoading,
            masterOrVariantOfReferencedSetProductsQueryResult2.isInitialLoading,
            masterOrVariantOfReferencedSetProductsQueryResult1.isError,
            masterOrVariantOfReferencedSetProductsQueryResult2.isError,
            masterOrVariantOfReferencedSetProductsQueryResult1.error,
            masterOrVariantOfReferencedSetProductsQueryResult2.error
        ]
    )

    // create a map of the master or variant of the referenced set products by their id
    const masterOrVariantOfReferencedSetProductsById = useMemo(
        () =>
            masterOrVariantOfReferencedSetProductsQueryResult.data?.data?.length > 0
                ? Object.fromEntries(
                      masterOrVariantOfReferencedSetProductsQueryResult.data.data.map((sp) => [
                          sp.id,
                          sp
                      ])
                  )
                : null,
        [masterOrVariantOfReferencedSetProductsQueryResult.data]
    )

    // unify the set products with their master or variant
    const productSetWithUnifiedSetProducts = useMemo(() => {
        if (!isProductSet(productSetWithLoadedReferencedSetProducts)) {
            return productSetWithLoadedReferencedSetProducts
        }

        const unify = (sp) => {
            const masterOrVariantId = masterOrVariantOfReferencedSetProducts?.[sp.id]
            const masterOrVariant = masterOrVariantId
                ? masterOrVariantOfReferencedSetProductsById?.[masterOrVariantId]
                : null
            if (!masterOrVariant) {
                return sp
            }
            if (isProductVariant(sp)) {
                return unifyProduct(masterOrVariant, sp)
            } else if (isProductMaster(sp)) {
                return unifyProduct(sp, masterOrVariant)
            }
            return sp
        }

        return {
            ...productSetWithLoadedReferencedSetProducts,
            setProducts: productSetWithLoadedReferencedSetProducts.setProducts.map(unify),
            ...(isCustomizableProductSet(productSetWithLoadedReferencedSetProducts)
                ? {
                      setExtensions:
                          productSetWithLoadedReferencedSetProducts.setExtensions?.map(unify),
                      setAccessories:
                          productSetWithLoadedReferencedSetProducts.setAccessories?.map(unify)
                  }
                : {})
        }
    }, [
        productSetWithLoadedReferencedSetProducts,
        masterOrVariantOfReferencedSetProducts,
        masterOrVariantOfReferencedSetProductsById
    ])

    const setProducts = useMemo(
        () =>
            [
                ...(productSetWithUnifiedSetProducts?.setProducts || []),
                ...(productSetWithUnifiedSetProducts?.setExtensions || []),
                ...(productSetWithUnifiedSetProducts?.setAccessories || [])
            ].filter(Boolean),
        [productSetWithUnifiedSetProducts]
    )
    const selectedSetProducts = useMemo(
        () => getProductsToAddToCart(productSetWithUnifiedSetProducts),
        [productSetWithUnifiedSetProducts]
    )
    const unselectedSetProducts = useMemo(
        () => setProducts.filter((p) => !selectedSetProducts.includes(p)),
        [setProducts, selectedSetProducts]
    )

    const priceParameters = useMemo(() => {
        if (!productSet) {
            return
        }
        // bypass requesting price when we are still loading the referenced set products
        if (referencedSetProductsQueryResult.isFetching) {
            return
        }
        const c_selected = selectedSetProducts
            .map((p) => p.id)
            .sort((a, b) => a.localeCompare(b))
            .join('|')
        const c_unselected = unselectedSetProducts
            .map((p) => p.id)
            .sort((a, b) => a.localeCompare(b))
            .join('|')
        return {
            locale: localeId,
            c_selected,
            ...(c_unselected && {c_unselected})
        }
    }, [
        localeId,
        productSet,
        referencedSetProductsQueryResult.isFetching,
        selectedSetProducts,
        unselectedSetProducts
    ])

    // promotions do not appear when loading product set with getProduct, so we need to fetch it in any case
    const needsFetchingCustomerPrice =
        !!productSet?.id && !!priceParameters && queryOptions?.enabled !== false

    const priceQueryResult = useProductSetFullPrices(
        {
            parameters: priceParameters
        },
        {
            keepPreviousData: false,
            ...queryOptions,
            enabled: needsFetchingCustomerPrice
        }
    )

    const productSetWithPrice = useMemo(() => {
        let product = productSetWithUnifiedSetProducts

        // update the price and promotions of the product set
        if (priceQueryResult.data?.selected?.price != null) {
            product = {
                ...product,
                ...priceQueryResult.data.selected
            }
        }

        if (isCustomizableProductSet(productSet)) {
            const newPriceResponse = priceQueryResult.data

            // update the set products
            const update = (setProduct) => {
                // use the correct list
                const list = selectedSetProducts.includes(setProduct)
                    ? newPriceResponse?.selected?.productLineItems
                    : newPriceResponse?.unselected
                // find the product line for the set product - look for the variant id as earlier we have selected the variant
                const productLine = list?.find((p) => p.variantId === setProduct.id)
                // update the set product with the price
                if (productLine) {
                    setProduct = {
                        ...setProduct,
                        price: productLine.price,
                        productPromotions: (
                            productLine.pliPromotions ||
                            productLine.promotions ||
                            []
                        ).map((promotionId) => ({
                            promotionId
                        }))
                    }
                }
                // add promotion for the set product
                if (productLine?.adjustedPrice != null) {
                    setProduct = {
                        ...setProduct,
                        productPromotions: [
                            {
                                promotionalPrice: productLine?.adjustedPrice
                            },
                            ...(setProduct.productPromotions || [])
                        ]
                    }
                }

                return setProduct
            }

            // update set products
            product = {
                ...product,
                setProducts: product.setProducts.map(update),
                setExtensions: product.setExtensions?.map(update),
                setAccessories: product.setAccessories?.map(update)
            }
        }

        return product
    }, [productSet, productSetWithUnifiedSetProducts, priceQueryResult.data, selectedSetProducts])

    return {
        master: productSetWithPrice,
        variant: null,
        isMasterLoading: isProductSetLoading,
        isVariantLoading:
            isProductSetLoading ||
            referencedSetProductsQueryResult.isFetching ||
            masterOrVariantOfReferencedSetProductsQueryResult.isInitialLoading,
        isPriceLoading:
            isProductSetLoading ||
            referencedSetProductsQueryResult.isFetching ||
            masterOrVariantOfReferencedSetProductsQueryResult.isInitialLoading ||
            (needsFetchingCustomerPrice &&
                // mark as loading on server and during hydration to avoid display of guest price to registered user
                (priceQueryResult.isLoading || isServer || !isHydrated()))
    }
}

/**
 * Custom hook to fetch complete product details (master and variant) suitable for product detail page
 *
 * Note: uses query search parameters to determine the currently selected variant
 *
 * @param {string} productId
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 */
const useProductDetail = (productId, queryOptions) => {
    const location = useLocation()
    const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search])

    // load the initial product
    const initialProductQueryResult = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PARAMETERS,
                id: urlParams.get('pid') || productId
            }
        },
        {
            keepPreviousData: true,
            ...queryOptions
        }
    )

    // handle errors inline as this will be executed server side and can return proper error status
    if (initialProductQueryResult.isError) {
        const errorStatus = initialProductQueryResult.error?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Product Not Found.')
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    const initialProduct = initialProductQueryResult.data

    const resultFromVariant = useProductDetailFromVariant(initialProductQueryResult, {
        ...queryOptions,
        enabled: queryOptions?.enabled !== false && isProductVariant(initialProduct)
    })
    const resultFromMaster = useProductDetailFromMaster(initialProductQueryResult, {
        ...queryOptions,
        enabled: queryOptions?.enabled !== false && isProductMaster(initialProduct)
    })
    const resultFromProductSet = useProductDetailFromProductSet(initialProductQueryResult, {
        ...queryOptions,
        enabled: queryOptions?.enabled !== false && isProductSet(initialProduct)
    })

    if (isProductVariant(initialProduct)) {
        return resultFromVariant
    }
    if (isProductMaster(initialProduct)) {
        return resultFromMaster
    }
    if (isProductSet(initialProduct)) {
        return resultFromProductSet
    }

    return {
        master: initialProduct,
        variant: null,
        isInitialLoading: initialProductQueryResult.isLoading,
        isMasterLoading: initialProductQueryResult.isLoading,
        isVariantLoading: initialProductQueryResult.isLoading,
        isPriceLoading: initialProductQueryResult.isLoading
    }
}

export default useProductDetail
